//@author: mandy
//https://ng-bootstrap.github.io/#/components/datepicker/examples
//https://github.com/ng-bootstrap/ng-bootstrap/issues/2391

import { NgClass, NgTemplateOutlet } from "@angular/common";
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    input,
    Input,
    linkedSignal,
    NgZone,
    OnChanges,
    OnInit,
    Output,
    signal,
    SimpleChanges,
    ViewChild,
    ViewEncapsulation,
    model,
    computed,
    effect,
    inject,
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { NgbCalendar, NgbDate, NgbDatepicker, NgbDateStruct, NgbInputDatepicker } from "@ng-bootstrap/ng-bootstrap";
import { S25Util } from "../../util/s25-util";
import { S25Datefilter } from "../s25-dateformat/s25.datefilter.service";
import { PreferenceService } from "../../services/preference.service";
import { S25Dateformat } from "../s25-dateformat/s25.dateformat.service";
import { Preference } from "../../pojo/PeferenceI";
import { Proto } from "../../pojo/Proto";
import ISODateString = Proto.ISODateString;
import { Nullable, PartialBy } from "../../pojo/Util";
import { FormValueControl } from "@angular/forms/signals";
import { AriaLabel } from "../../util/aria/aria.label";
import { S25TriggerRerenderDirective } from "../s25-trigger-rerender/s25.trigger.rerender.directive";

@Component({
    selector: "s25-datepicker",
    template: `
        @if (inline && init && !multipleDate) {
            <div id="inline{{ id }}">
                @if (readonly()) {
                    {{ finalModelValue }}
                } @else {
                    <ngb-datepicker
                        *s25-ng-trigger-rerender="inlinePickerRenderKey()"
                        #dp
                        [(ngModel)]="model"
                        (navigate)="date = $event.next"
                        [footerTemplate]="footerTemplate"
                        [startDate]="date"
                        (dateSelect)="onChangeHandler(model)"
                        [minDate]="minDateObj"
                        [maxDate]="maxDateObj"
                        [firstDayOfWeek]="firstDayOfWeek"
                        [disabled]="disabled()"
                    />
                }
            </div>
        }

        @if (!inline && init) {
            <div id="popup{{ id }}" class="form-group" (keydown.escape)="handleEscKey($event)">
                @if (inputLabel) {
                    <label [for]="id" class="ngBold c-margin-right--quarter">{{ inputLabel }}</label>
                }
                @if (readonly()) {
                    {{ finalModelValue }}
                } @else {
                    <input
                        class="c-input"
                        [ngClass]="inputClass"
                        name="dp"
                        placement="bottom"
                        ngbDatepicker
                        #d="ngbDatepicker"
                        (dateSelect)="onDateSelect($event)"
                        [footerTemplate]="footerTemplate"
                        [value]="allowEmpty ? finalModelValue || '' : finalModelValue"
                        [startDate]="date"
                        (click)="d.toggle()"
                        (keydown.enter)="toggle($event)"
                        (keydown.space)="$event.preventDefault(); toggle($event)"
                        [id]="id"
                        (change)="onChange($event)"
                        (closed)="onCloseHandler()"
                        [minDate]="minDateObj"
                        [maxDate]="maxDateObj"
                        [container]="popoverOnBody ? 'body' : null"
                        [placeholder]="placeholder"
                        [firstDayOfWeek]="firstDayOfWeek"
                        [attr.aria-label]="ariaLabelledBy() ? null : ariaLabelText() || inputLabel || null"
                        [attr.aria-labelledby]="ariaLabelledBy() || null"
                        [disabled]="disabled()"
                        autocomplete="off"
                    />
                }
            </div>
        }

        @if (inline && init && multipleDate) {
            <div id="inline{{ id }}">
                <ngb-datepicker
                    [(ngModel)]="model"
                    (navigate)="date = $event.next"
                    [dayTemplate]="customDay"
                    [startDate]="date"
                    (dateSelect)="onChangeHandler(model)"
                    [minDate]="minDateObj"
                    [maxDate]="maxDateObj"
                    [firstDayOfWeek]="firstDayOfWeek"
                />
                <ng-template
                    #customDay
                    let-date
                    let-currentMonth="currentMonth"
                    let-currentYear="currentYear"
                    let-selected="selected"
                >
                    <ng-container [ngTemplateOutlet]="isChoose(date) ? chosen : notChosen" />
                    <ng-template #chosen>
                        <div class="custom-day aw-button--primary">{{ date.day }}</div>
                    </ng-template>
                    <ng-template #notChosen>
                        <div class="custom-day" [class.text-muted]="date.month !== currentMonth">{{ date.day }}</div>
                    </ng-template>
                </ng-template>
            </div>
        }

        <ng-template #footerTemplate>
            @if (showToday()) {
                <div class="col text-center c-margin-bottom--quarter">
                    <a
                        class="s25-ng divTodayLink"
                        tabindex="0"
                        (click)="onSelectTodayFooterClick()"
                        (keydown.enter)="onSelectTodayFooterClick()"
                        >Select Today</a
                    >
                </div>
            }
        </ng-template>
    `,
    styles: `
        .editable-datepicker {
            background-color: inherit;
            border: none !important;
            box-shadow: none;
        }

        .ngb-dp-today {
            font-weight: bold;
        }

        /* so that it appears above modals :upside_down_smile: */
        ngb-datepicker.dropdown-menu {
            z-index: 1060;
        }
    `,
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [NgbDatepicker, S25TriggerRerenderDirective, FormsModule, NgClass, NgbInputDatepicker, NgTemplateOutlet],
})
export class S25DatepickerComponent implements OnInit, OnChanges, FormValueControl<Nullable<number | string | Date>> {
    static count: number = 0;

    readonly value = model<Nullable<number | string | Date>>();
    readonly parsedValue = computed(() => S25Util.date.parse(this.value()));

    @Input() inputLabel?: string;
    readonly ariaLabel = input<AriaLabel>();
    @Input() inputId?: string;
    @Input() inputClass: string;
    readonly showToday = input<boolean>(false);
    readonly occurrencesDates = input<(Date | ISODateString)[]>();
    @Input() prefType: Preference.PrefType = "U";
    @Input() inline: boolean = false;
    @Input() multipleDate: boolean = false;
    @Input() onClose: Function;
    @Input() minDate?: Date;
    @Input() maxDate?: Date;
    @Input() autoSelectToday?: boolean = true;
    @Input() popoverOnBody?: boolean = false;
    @Input() allowEmpty?: boolean = false;
    @Output() forceClose = new EventEmitter<void>();
    @Input() placeholder?: string = "Select a date";
    @Input() isDateSelected?: (date: NgbDate) => boolean; // Optional callback for checking whether a date is selected
    readonly readonly = input(false);
    readonly disabled = input(false);

    // Updated in the datepicker API
    readonly inputDate = linkedSignal(this.parsedValue);
    readonly ariaLabelledBy = computed(() => AriaLabel.labelledBy(this.ariaLabel()));
    readonly ariaLabelText = computed(() => AriaLabel.text(this.ariaLabel()));

    datepicker: NgbInputDatepicker;
    date: PartialBy<NgbDateStruct, "day">;
    minDateObj: any;
    maxDateObj: any;

    @ViewChild(NgbInputDatepicker, { static: false })
    set componentDatepicker(datepicker: NgbInputDatepicker) {
        this.datepicker = datepicker;
        if (this.initOpen) {
            this.open();
        }
    }

    init = false;
    initOpen = false;
    dateOrder: string = "MDY";
    dateSep: string;
    month: string;
    day: string;
    id: string;
    initModelValue: Date;
    finalModelValue: any;
    dateFormat: any;
    model: PartialBy<NgbDateStruct, "day"> | null; // null clears inline ngb-datepicker selection (aria-selected)
    firstDayOfWeek: number = 7;

    /**
     * ng-bootstrap's `NgbDatepicker` does not clear its internal `selectedDate` when `ngModel` is set to `null`
     * (see https://github.com/ng-bootstrap/ng-bootstrap/issues/...): grid `aria-selected="true"` persists.
     * Incrementing this key when the bound value clears and using `*s25-ng-trigger-rerender` remounts the inline
     * `ngb-datepicker`, which is the only reliable way to drop the highlight.
     */
    readonly inlinePickerRenderKey = signal(0);

    private readonly cd = inject(ChangeDetectorRef);
    private readonly parentChangeDetectorRef = inject(ChangeDetectorRef, { skipSelf: true, optional: true });

    constructor(
        private elementRef: ElementRef,
        private zone: NgZone,
        private calendar: NgbCalendar,
    ) {
        this.elementRef.nativeElement.angBridge = this; //bridge to AngularJS; used for AngJS to set model values and call setter fns
        S25DatepickerComponent.count++;
        this.id = "date-" + S25DatepickerComponent.count;

        effect(() => {
            this.value();
            if (!this.init) {
                return;
            }
            this.refreshPickerFromBoundValue();
            this.scheduleParentCdRefreshAfterExternalBindChange();
        });
    }

    /**
     * OnPush parents that bind `[(value)]` to a plain field need a follow-up CD pass: the effect runs in a microtask
     * after the parent's CD cycle finished, so model / finalModelValue writes are not yet rendered. Matches the
     * `markForCheck` + `queueMicrotask(detectChanges)` pattern that worked when callers did it themselves.
     */
    private scheduleParentCdRefreshAfterExternalBindChange(): void {
        const parentCd = this.parentChangeDetectorRef;
        if (!parentCd) {
            return;
        }
        parentCd.markForCheck();
        queueMicrotask(() => parentCd.detectChanges());
    }

    /** When the bound `value` changes from outside (undefined / null / new date), sync the widget and run `refresh()`. */
    private refreshPickerFromBoundValue(): void {
        const raw = this.value();
        if (this.boundValueIsEmpty(raw)) {
            this.model = null as unknown as PartialBy<NgbDateStruct, "day"> | null;
            this.finalModelValue = "";
            this.date = { year: null, month: null, day: null } as unknown as PartialBy<NgbDateStruct, "day">;
            const inputEl = this.elementRef.nativeElement.querySelector("input") as HTMLInputElement | null;
            if (inputEl) {
                inputEl.value = "";
            }
            if (this.inline && !this.multipleDate) {
                this.inlinePickerRenderKey.update((key) => key + 1);
            }
            this.refresh();
            return;
        }
        const parsed = S25Util.date.parse(raw as string | number | Date);
        if (!parsed || !S25Util.date.isValid(parsed)) {
            this.refresh();
            return;
        }
        const ymd = S25Util.date.toYMD(parsed) as NgbDateStruct;
        if (ymd.year == null || ymd.month == null || ymd.day == null) {
            this.refresh();
            return;
        }
        const matchesCurrent =
            this.model && this.model.year === ymd.year && this.model.month === ymd.month && this.model.day === ymd.day;
        if (!matchesCurrent) {
            this.selectDate(ymd, true);
        } else {
            this.refresh();
        }
    }

    private boundValueIsEmpty(raw: Nullable<number | string | Date>): boolean {
        if (raw === null || raw === undefined) {
            return true;
        }
        if (typeof raw === "string") {
            return raw.trim() === "";
        }
        return false;
    }

    ngOnInit() {
        this.minDateObj = S25Util.date.toYMD(this.minDate);
        this.maxDateObj = S25Util.date.toYMD(this.maxDate);
        this.zone.run(() => {
            this.getPrefDateOrder().then(() => {
                if (
                    this.inputDate() &&
                    (typeof this.inputDate() === "string" || S25Util.date.isValid(this.inputDate()))
                ) {
                    // can be Date | string
                    const date = <NgbDateStruct>S25Util.date.toYMD(this.inputDate());
                    if (date && date.year && date.month && date.day) {
                        this.date = date;
                        this.selectDate(this.date, true);
                    }
                }
                if (this.inline && !this.multipleDate && this.autoSelectToday) this.selectToday();
                this.inputId ? (this.id = this.inputId) : "";
                this.refresh();
            });
        });
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.minDate) {
            this.minDateObj = S25Util.date.toYMD(this.minDate);
        }
    }

    selectDate(date: PartialBy<NgbDateStruct, "day">, skipEmit?: boolean) {
        this.model = date;
        this.onDateSelect(date, skipEmit);
    }

    /*
    For text entry, hitting enter needs to
        EITHER save the entered date, OR
        open the datepicker.
    */
    toggle(e: any) {
        const inputEmpty = !e.target.value;
        if (inputEmpty && !this.finalModelValue) {
            this.datepicker.open();
            return;
        }

        if (inputEmpty && this.allowEmpty) {
            this.finalModelValue = "";
            this.onChangeHandler("");
            return;
        }

        const newDt = S25Util.date.getDate(e.target.value);
        if (S25Util.date.equal(newDt, this.finalModelValue)) {
            this.datepicker.open();
        } else {
            this.onChangeHandler(S25Util.date.toYMD(newDt));
        }
    }

    getStartDate() {
        const d = S25Util.date.getDate(this.finalModelValue);
        this.date = { year: d.getFullYear(), month: d.getMonth() + 1, day: d.getDate() };
    }

    //inline get today/select today
    selectToday() {
        this.selectDate(this.calendar.getToday());
    }

    /** Footer "Select Today": for inline mode do not run `close()` (it removes the inline calendar from the DOM). */
    onSelectTodayFooterClick() {
        this.selectToday();
        if (!this.inline) {
            this.close();
        }
    }

    getPrefDateOrder() {
        return S25Util.all({
            pref: PreferenceService.getPreferences(
                ["DateOrder", "DateSep", "Month", "Day", "CalendarView"],
                this.prefType,
            ),
        }).then((resp) => {
            // Use defaults if preferences are undefined
            this.dateOrder = resp.pref.DateOrder?.value ?? S25Datefilter.DEFAULT_DATE_PREFERENCES.DateOrder.value;
            this.dateSep = resp.pref.DateSep?.value ?? S25Datefilter.DEFAULT_DATE_PREFERENCES.DateSep.value;
            this.month = resp.pref.Month?.value ?? S25Datefilter.DEFAULT_DATE_PREFERENCES.Month.value;
            this.day = resp.pref.Day?.value ?? S25Datefilter.DEFAULT_DATE_PREFERENCES.Day.value;
            this.dateFormat = this.getDateFormat();

            if (this.inputDate()) {
                this.finalModelValue = S25Datefilter.transform(this.inputDate(), this.dateFormat);
            }

            this.initModelValue = S25Util.date.clone(this.inputDate());
            this.firstDayOfWeek = Number(
                resp.pref.CalendarView?.value ?? S25Datefilter.DEFAULT_DATE_PREFERENCES.CalendarView.value,
            );
            // this.modelValue && this.modelValue.date && this.onChangeHandler({year: this.modelValue.date.getFullYear(),
            //                                                                 month: this.modelValue.date.getMonth(),
            //                                                                 day: this.modelValue.date.getDate() });
            this.init = true;
            this.refreshPickerFromBoundValue();
        });
    }

    // get user/system dateformat
    getDateFormat() {
        const dateFormat = S25Dateformat.getDateformat({
            dateorder: this.dateOrder,
            datesep: this.dateSep,
            day: this.day,
            month: this.day,
        });
        return dateFormat;
    }

    onChangeHandler(newValue: any, skipEmit?: boolean) {
        if (newValue) {
            const d = S25Util.date.createDate(newValue.year, newValue.month, newValue.day);
            this.finalModelValue = S25Datefilter.transform(d, this.dateFormat);
            this.getStartDate();
            if (!skipEmit) {
                this.value.set(d);
            }
        }

        const input = this.elementRef.nativeElement.querySelector("input");
        if (input && input.value === "") {
            this.elementRef.nativeElement.querySelector("input").value = this.finalModelValue;
            this.cd.detectChanges();
        }
    }

    //change NgbDatePicker format, tried to use NgbDatePicker Custom date adapter and formatter, doesn't work 25live dateformat
    onDateSelect(newValue: any, skipEmit?: boolean) {
        this.onChangeHandler(newValue, skipEmit);
        this.refresh();
    }

    //enter date input
    onChange(e: any) {
        const isValid = S25Util.date.isValid(e.target.value);
        if (!e.target.value && this.allowEmpty) {
            this.value.set(null);
            return;
        }
        if (isValid) {
            const dt = S25Util.date.getDate(e.target.value);
            this.onChangeHandler(S25Util.date.toYMD(dt));
        } else {
            this.selectToday();
            this.refresh();
        }
    }

    /// cuz not use [(ngModel)], close datepicker work around
    close = () => {
        this.forceClose.emit();
        if (this.elementRef.nativeElement.querySelector("ngb-datepicker") !== null) {
            this.elementRef.nativeElement.querySelector("ngb-datepicker").remove("show");
        }
        this.elementRef.nativeElement.querySelector("input")?.click();
        this.elementRef.nativeElement.querySelector("input")?.focus();
    };

    isChoose(date: NgbDate) {
        if (this.isDateSelected) return this.isDateSelected(date);
        const occurrences = this.occurrencesDates();
        if (occurrences && occurrences.length) {
            for (let i = 0; i < occurrences.length; i++) {
                const selectedDate = new Date(occurrences[i]);
                const { year, month, day } = S25Util.date.toYMD(selectedDate);
                if (day === date.day && month === date.month && year === date.year) {
                    return true;
                }
            }
        }
    }

    open = () => {
        if (!this.datepicker) {
            this.initOpen = true;
        } else {
            if (!this.datepicker.isOpen()) {
                this.datepicker.open();
            }
        }
    };

    onCloseHandler = () => {
        this.onClose && this.onClose();
    };

    refresh = () => {
        try {
            this.cd.detectChanges();
        } catch (err: any) {}
    };

    // if using esc key to close calendar dropdown in a modal, stop modal from closing as well
    handleEscKey(event: Event) {
        if (this.datepicker?.isOpen()) {
            event.stopPropagation();
            this.close();
        }
    }
}
